import { Button, Input } from '@dispatch-ui/react';
import { useState } from 'react';
import MainContentCard from '../../components/layout/main-layout/main-content-card';
import MultipleApplicationsAlert from '../../components/multiple-applications-alert/multiple-applications-alert';
import Spinner from '../../components/spinner';
import Typography from '../../components/typography/typography';
import { OPENINGS_WAITLIST_URL } from '../../constants/util.constants';
import useDebounce from '../../hooks/use-debounce';
import useInfiniteScroll from '../../hooks/use-infinite-scroll';
import { useTranslation } from '../../hooks/use-translation';
import { Opening } from '../../types/opening.types';
import OpeningsList from './openings-list';
import fetchOpenings from './openings-service';

function AvailableOpeningsPage(): JSX.Element {
  const [textSearch, setTextSearch] = useState<string>('');
  const debouncedSearch = useDebounce(textSearch, 300);

  const { t } = useTranslation('openings');

  const {
    data: availableOpenings,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    bottomElementRef,
  } = useInfiniteScroll<Opening>({
    queryKey: ['openings'],
    queryFn: async (page: number, searchTerm?: string) => {
      const result = await fetchOpenings(searchTerm || '', page);
      return result
        ? { data: result.openings, page_info: result.page_info }
        : undefined;
    },
    searchTerm: debouncedSearch,
  });

  const handleTextSearchValue: React.ChangeEventHandler<
    HTMLInputElement
  > = e => {
    setTextSearch(e.target.value);
  };

  return (
    <MainContentCard
      title={t('OPENINGS_PAGE_TITLE')}
      alert={<MultipleApplicationsAlert />}
      content={
        <div className="mt-2">
          <Typography size="sm" color="black">
            {t('SUBTITLE1')}
          </Typography>
          <Typography size="sm" color="black">
            {t('SUBTITLE2')}
          </Typography>
          <Typography size="sm" color="black">
            {t('SUBTITLE3')}
            &nbsp;
            <a
              data-testid="join-waitlist-link"
              rel="noreferrer"
              href={OPENINGS_WAITLIST_URL}
              className="text-blue visited:text-purple-800"
              target="_blank"
            >
              {t('JOIN_OUR_WAIT_LIST')}
            </a>
            !
          </Typography>

          <div className="mb-8 mt-4 space-y-2">
            <Typography size="sm" fontWeight="semibold">
              {t('TEXT_SEARCH_LABEL')}
            </Typography>
            <Input
              data-testid="text-search-input"
              placeholder={t('TEXT_SEARCH_PLACEHOLDER')}
              type="text"
              title={textSearch}
              onChange={handleTextSearchValue}
            />
          </div>
          <div>
            {isLoading && availableOpenings.length === 0 && (
              <div className="flex w-full justify-center">
                <Spinner />
              </div>
            )}
            {availableOpenings.length > 0 && (
              <OpeningsList availableOpenings={availableOpenings} />
            )}
            {!isLoading && availableOpenings.length === 0 && (
              <div className="flex w-full justify-center text-gray-600">
                <Typography
                  size="sm"
                  color="gray-600"
                  className="max-w-[444px] text-center"
                >
                  {t('DONT_SEE_OPPORTUNITIES_1')}
                  &nbsp;
                  <a
                    data-testid="join-waitlist-link-no-match"
                    rel="noreferrer"
                    href={OPENINGS_WAITLIST_URL}
                    className="text-blue visited:text-purple-800"
                    target="_blank"
                  >
                    {t('JOIN_OUR_WAIT_LIST')}
                  </a>
                  &nbsp;
                  {t('DONT_SEE_OPPORTUNITIES_2')}
                </Typography>
              </div>
            )}
            {hasNextPage &&
              !isFetchingNextPage &&
              availableOpenings.length > 0 && (
                <div className="flex w-full justify-center py-4">
                  <Button
                    data-testid="load-more-button"
                    variant="outline"
                    onClick={fetchNextPage}
                    className="px-6 py-2"
                  >
                    {t('LOAD_MORE')}
                  </Button>
                </div>
              )}
            {isFetchingNextPage && (
              <div className="flex w-full justify-center py-4">
                <Spinner />
              </div>
            )}
            {/* Invisible element to trigger infinite scroll */}
            <div
              ref={bottomElementRef}
              className="h-1"
              data-testid="infinite-scroll-trigger"
            />
          </div>
        </div>
      }
      footer={
        (availableOpenings || []).length > 0 ? (
          <div className="flex w-full justify-center text-gray-600">
            <Typography
              size="sm"
              color="gray-600"
              className="max-w-[444px] text-center"
            >
              {t('DONT_SEE_OPPORTUNITIES_1')}
              &nbsp;
              <a
                data-testid="join-waitlist-link-footer"
                rel="noreferrer"
                href={OPENINGS_WAITLIST_URL}
                className="text-blue visited:text-purple-800"
                target="_blank"
              >
                {t('JOIN_OUR_WAIT_LIST')}
              </a>
              &nbsp;
              {t('DONT_SEE_OPPORTUNITIES_2')}
            </Typography>
          </div>
        ) : (
          <div />
        )
      }
    />
  );
}

export default AvailableOpeningsPage;
